/**
 * Raporti i kohës së një projekti — një faqosje, tri dalje.
 *
 * Faqosja përshkruhet NJË herë dhe e lexojnë Excel-i, PDF-ja dhe printimi nga
 * `reportSheetExport` — e njëjta që nxjerr Listën e Pagave dhe raportet e tjera.
 * Prandaj ky dokument duket si çdo dokument tjetër që del nga Nexuma: kokë
 * kompanie, titull navy, rreshta zebra, TOTALI dhe kuti përmbledhëse.
 *
 * ── Pse një kolonë «Burimi» dhe jo dy tabela ──────────────────────────────
 * «Caktimet» dhe «Nga orari» janë dy burime plani për të njëjtat orë: e para
 * thotë sa orë në ditë i janë caktuar dikujt, e dyta sa nga turni i tij i
 * kushtohet këtij projekti. Mbledhja e tyre do t'i numëronte dy herë. Prandaj
 * rrinë në të njëjtën tabelë por të shënuara veç — dhe totali llogaritet për
 * secilin burim te kutia përmbledhëse, kurrë si një shifër e vetme.
 */
import { exportSheetExcel, downloadSheetPdf, printSheet } from './reportSheetExport';
import { fmtDate } from '../components/projects/projectConstants';

const h1 = (n) => Math.round((Number(n) || 0) * 10) / 10;
/** Orë me njësinë pranë: «36 orë», «144.5 orë» — kurrë valutë. */
const hoursText = (n) => `${h1(n)} orë`;

/** Rreshtat e të dy burimeve, të shënuar se nga vijnë. */
function timeRows(summary) {
  const a = (summary?.assignments || []).map((x) => ({
    source: 'Caktim',
    name: x.name || '—',
    detail: x.position || '—',
    perDay: `${h1(x.hoursPerDay)} orë/ditë`,
    from: fmtDate(x.startDate),
    to: fmtDate(x.endDate),
    // Ditët e punës për caktimin, segmentet për orarin — njësi të ndryshme te
    // e njëjta kolonë, ndaj njësia shkruhet me vlerën.
    span: `${x.workdays} ditë pune`,
    hours: hoursText(x.plannedHours),
  }));
  const s = (summary?.schedule || []).map((x) => ({
    source: 'Orari',
    name: x.name || '—',
    detail: '—',
    perDay: '—',
    from: fmtDate(x.fromDate),
    to: x.toDate ? fmtDate(x.toDate) : 'pa afat',
    span: `${x.segments} segmente`,
    hours: hoursText(x.hours),
  }));
  return [...a, ...s];
}

const fileBase = (project) => {
  const slug = String(project?.name || project?.Name || 'raport')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `projekt-${slug || 'raport'}-koha`;
};

/**
 * Faqosja e plotë. `company` kalohet nga thirrësi kur e di; pa të, koka mban
 * vetëm projektin — më mirë kokë e shkurtër se një emër kompanie i shpikur.
 */
export function projectTimeLayout(project, summary, { company = {} } = {}) {
  const tl = summary?.timeline || {};
  const h = summary?.hours || {};
  const rows = timeRows(summary);
  const name = project?.name || project?.Name || 'Projekt';

  const deadline = tl.hasDates
    ? `${fmtDate(tl.startDate)} → ${fmtDate(tl.endDate)}`
    : 'Pa datë fillimi dhe mbarimi';
  const deadlineState = tl.hasDates && tl.remainingDays !== null && tl.remainingDays !== undefined
    ? (tl.overdue ? `${Math.abs(tl.remainingDays)} ditë vonesë` : `${tl.remainingDays} ditë të mbetura`)
    : '';

  return {
    title: 'KOHA E PROJEKTIT',
    logo: company.Logo || null,
    left: [
      company.CompanyName || company.name || '',
      name,
      project?.code || project?.Code ? `Kodi: ${project.code || project.Code}` : '',
      project?.description || project?.Description || '',
    ].filter(Boolean),
    right: [
      `Afati: ${deadline}`,
      deadlineState,
      `Orë të regjistruara: ${hoursText(h.logged)}`,
      h.budget ? `Buxheti: ${hoursText(h.budget)}` : 'Buxheti: —',
    ].filter(Boolean),
    // Shënimi shkruhet vetëm kur ka çka të thotë: pa të, një «0 orë» te dokumenti
    // do të lexohej si «askush s'ka punuar», që nuk është e vërteta.
    note: h.trackingInUse
      ? ''
      : 'Regjistrimi i orëve për projekte nuk është në përdorim; rreshtat më poshtë janë PLAN, jo orë të punuara.',
    columns: [
      { key: 'source', label: 'Burimi',    w: 10, pdfW: 16 },
      { key: 'name',   label: 'Punonjësi', w: 24 },
      { key: 'detail', label: 'Pozita',    w: 18, muted: true },
      { key: 'perDay', label: 'Norma',     w: 14, muted: true },
      { key: 'from',   label: 'Prej',      w: 13 },
      { key: 'to',     label: 'Deri',      w: 13 },
      { key: 'span',   label: 'Periudha',  w: 14, muted: true },
      { key: 'hours',  label: 'Orë',       w: 10, bold: true, align: 'right' },
    ],
    body: rows,
    // Pa total të përgjithshëm: dy burimet nuk mblidhen. Shifrat e tyre janë te
    // kutia përmbledhëse, secila me emrin e vet.
    totals: null,
    summaryTitle: 'Përmbledhje',
    // `money: true` te `reportSheetExport` do të thotë valutë — shton «€» te PDF-ja
    // dhe formatin monetar te Excel-i. Këto janë ORË, ndaj njësia shkruhet vetë.
    summary: [
      { label: 'Orë të regjistruara', value: hoursText(h.logged) },
      { label: 'Buxheti i orëve',     value: h.budget ? hoursText(h.budget) : '—' },
      { label: 'Plani i caktimeve',   value: hoursText(h.plannedFromAssignments) },
      { label: 'Nga orari i punës',   value: hoursText(h.scheduleHours) },
    ],
    signature: {
      left: 'Data: ______________',
      right: 'Nënshkrimi i menaxherit',
    },
    footer: [company.CompanyName || company.name, name, 'Koha e projektit'].filter(Boolean).join(' · '),
    sheetName: 'Koha e projektit',
    fileBase: fileBase(project),
    orientation: 'landscape',
  };
}

export const exportProjectTimeExcel = (project, summary, opts) =>
  exportSheetExcel(projectTimeLayout(project, summary, opts));

/** Shkarkon një skedar `.pdf` — jo dritare printimi. */
export const exportProjectTimePdf = (project, summary, opts) =>
  downloadSheetPdf(projectTimeLayout(project, summary, opts));

/** Hap të njëjtin dokument me dialogun e printimit të gatshëm. */
export const printProjectTime = (project, summary, opts) =>
  printSheet(projectTimeLayout(project, summary, opts));
